import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Hammer, FlaskConical, CheckCircle2, XCircle, Image, X } from 'lucide-react';
import { evidenceApi } from '../../api/evidence';
import type { Evidence } from '../../api/evidence';

interface Props {
  epicIds: string[];
  batchId?: string | null;
  sprintStatus?: string;
}

interface BuildResultData {
  status: string;
  services?: Array<{ name: string; healthy: boolean }>;
  duration?: number;
}

interface E2ETestData {
  testResults?: {
    framework?: string;
    status?: string;
    passed?: number;
    failed?: number;
    skipped?: number;
    duration?: number;
    artifacts?: string[];
  };
}

interface ScreenshotArtifactsData {
  screenshotFiles?: string[];
  count?: number;
  batchId?: string;
  epicId?: string;
}

function formatDuration(ms?: number): string {
  if (!ms) return '';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function ServiceDot({ name, healthy }: { name: string; healthy: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full shrink-0 ${healthy ? 'bg-emerald-500' : 'bg-red-500'}`} />
      <span className="text-[10px] font-mono text-neutral-600 truncate">{name}</span>
    </div>
  );
}

function ScreenshotLightbox({ src, onClose }: { src: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white/70 hover:text-white"
        onClick={onClose}
      >
        <X size={24} />
      </button>
      <img
        src={src}
        alt="Test screenshot"
        className="max-w-full max-h-full object-contain rounded shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

function EpicEvidencePanel({ epicId, batchId }: { epicId: string; batchId?: string | null }) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const { data: buildData } = useQuery({
    queryKey: ['evidence', epicId, 'BUILD'],
    queryFn: () => evidenceApi.getEvidence(epicId, { stage: 'BUILD' }),
    staleTime: 30_000,
  });

  const { data: testingData } = useQuery({
    queryKey: ['evidence', epicId, 'TESTING'],
    queryFn: () => evidenceApi.getEvidence(epicId, { stage: 'TESTING' }),
    staleTime: 30_000,
  });

  const buildEvent = buildData?.evidence?.find((e: Evidence) => e.evidenceType === 'build_result');
  const testingEvent = testingData?.evidence?.find((e: Evidence) => e.evidenceType === 'e2e_test_results');
  const screenshotEvent = testingData?.evidence?.find((e: Evidence) => e.evidenceType === 'screenshot_artifacts');

  const buildResult = buildEvent?.evidenceData as BuildResultData | undefined;
  const e2eData = testingEvent?.evidenceData as E2ETestData | undefined;
  const testResults = e2eData?.testResults;
  const screenshotData = screenshotEvent?.evidenceData as ScreenshotArtifactsData | undefined;

  if (!buildEvent && !testingEvent && !screenshotEvent) return null;

  const buildPassed = buildResult?.status === 'success';
  const services = buildResult?.services ?? [];
  // Prefer screenshot_artifacts.screenshotFiles[] (always unique filenames) over
  // e2e_test_results.artifacts[] which may have duplicate basenames from older sprints
  const artifacts = (screenshotData?.screenshotFiles?.length ?? 0) > 0
    ? (screenshotData!.screenshotFiles!)
    : (testResults?.artifacts ?? []);
  const total = (testResults?.passed ?? 0) + (testResults?.failed ?? 0) + (testResults?.skipped ?? 0);
  const allPassed = testResults && testResults.failed === 0 && total > 0;
  const anyFailed = (testResults?.failed ?? 0) > 0;

  return (
    <>
      <div className="space-y-3">
        {/* Epic ID label */}
        <div className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 font-mono">{epicId}</div>

        {/* Build Health */}
        {buildResult && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-neutral-500">Build</span>
              <div className="flex items-center gap-2">
                {buildResult.duration ? (
                  <span className="text-[9px] font-mono text-neutral-400">{formatDuration(buildResult.duration)}</span>
                ) : null}
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                  buildPassed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                }`}>
                  {buildPassed ? 'PASSED' : 'FAILED'}
                </span>
              </div>
            </div>
            {services.length > 0 && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {services.map((svc) => (
                  <ServiceDot key={svc.name} name={svc.name} healthy={svc.healthy} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* E2E Results */}
        {testResults && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-neutral-500">E2E</span>
                {testResults.framework && (
                  <span className="text-[8px] font-bold uppercase tracking-wider px-1 py-px rounded bg-neutral-100 text-neutral-500">
                    {testResults.framework}
                  </span>
                )}
              </div>
              {testResults.duration ? (
                <span className="text-[9px] font-mono text-neutral-400">{formatDuration(testResults.duration)}</span>
              ) : null}
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <CheckCircle2 size={10} className="text-emerald-500" />
                <span className="text-[10px] font-mono tabular-nums text-emerald-600">{testResults.passed ?? 0}</span>
              </div>
              <div className="flex items-center gap-1">
                <XCircle size={10} className="text-red-500" />
                <span className="text-[10px] font-mono tabular-nums text-red-600">{testResults.failed ?? 0}</span>
              </div>
              {(testResults.skipped ?? 0) > 0 && (
                <span className="text-[9px] text-neutral-400 font-mono tabular-nums">
                  {testResults.skipped} skipped
                </span>
              )}
            </div>

            {allPassed && (
              <div className="text-[9px] font-medium text-emerald-700 bg-emerald-50 rounded px-2 py-1">
                All {total} tests passed
              </div>
            )}
            {anyFailed && (
              <div className="text-[9px] font-medium text-red-700 bg-red-50 rounded px-2 py-1">
                {testResults.failed} test{testResults.failed !== 1 ? 's' : ''} failed
              </div>
            )}
          </div>
        )}

        {/* Screenshot grid */}
        {batchId && artifacts.length > 0 && (
          <div className="space-y-1.5">
            <span className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Screenshots</span>
            <div className="grid grid-cols-4 gap-1.5">
              {artifacts.slice(0, 12).map((filename) => {
                const src = `/api/orchestration/artifacts/${batchId}/${epicId}/${filename}`;
                return (
                  <button
                    key={filename}
                    className="aspect-[4/3] rounded overflow-hidden border border-neutral-200 bg-neutral-100 hover:border-coral-300 transition-colors group relative"
                    onClick={() => setLightboxSrc(src)}
                    title={filename}
                  >
                    <img
                      src={src}
                      alt={filename}
                      className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Image size={12} className="text-white drop-shadow" />
                    </div>
                  </button>
                );
              })}
            </div>
            {artifacts.length > 12 && (
              <p className="text-[9px] text-neutral-400">+{artifacts.length - 12} more</p>
            )}
          </div>
        )}
      </div>

      {lightboxSrc && (
        <ScreenshotLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      )}
    </>
  );
}

function SkeletonRow() {
  return (
    <div className="space-y-2 animate-pulse">
      <div className="h-3 w-16 bg-neutral-100 rounded" />
      <div className="flex justify-between">
        <div className="h-3 w-24 bg-neutral-100 rounded" />
        <div className="h-3 w-16 bg-neutral-100 rounded" />
      </div>
    </div>
  );
}

export function PostBuildEvidenceCard({ epicIds, batchId, sprintStatus: _sprintStatus }: Props) {
  const firstEpicId = epicIds[0];
  const { data: firstBuildData, isLoading } = useQuery({
    queryKey: ['evidence', firstEpicId, 'BUILD'],
    queryFn: () => evidenceApi.getEvidence(firstEpicId, { stage: 'BUILD' }),
    staleTime: 30_000,
    enabled: !!firstEpicId,
  });

  const hasAnyBuildEvidence = (firstBuildData?.evidence?.length ?? 0) > 0;

  if (!firstEpicId) return null;

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b border-neutral-100 flex items-center gap-2">
          <Hammer size={11} className="text-neutral-400" />
          <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Post-Build Evidence</p>
        </div>
        <div className="px-4 py-3 space-y-3">
          <SkeletonRow />
          <SkeletonRow />
        </div>
      </div>
    );
  }

  if (!hasAnyBuildEvidence) return null;

  return (
    <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b border-neutral-100 flex items-center gap-2">
        <Hammer size={11} className="text-neutral-400" />
        <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Post-Build Evidence</p>
      </div>
      <div className="px-4 py-3 space-y-4 divide-y divide-neutral-100">
        {epicIds.map((epicId, i) => (
          <div key={epicId} className={i > 0 ? 'pt-4' : ''}>
            <EpicEvidencePanel epicId={epicId} batchId={batchId} />
          </div>
        ))}
      </div>
    </div>
  );
}
